import { z } from "zod";
import { Area, EntityId, EntityKind, Folder, Note, Project, Reminder, Task } from "./entities";
import { ContentConflict } from "./history";
import { MutationEnvelope } from "./mutations";

/**
 * 2: notes and task descriptions carry an editor document beside their Markdown
 * (`notes.updateContent`, `tasks.setDescriptionContent`), which a version 1 client cannot read.
 */
export const SYNC_PROTOCOL_VERSION = 2;

const change = <K extends EntityKind, S extends z.ZodTypeAny>(entity: K, row: S) =>
  z.object({
    /** `changes.seq` on the server: the monotonic cursor. */
    seq: z.number().int().positive(),
    entity: z.literal(entity),
    id: EntityId,
    /** The device whose mutation produced this change. A client uses it to recognise its own echoes. */
    deviceId: z.string().min(1),
    /** Full snapshot of the entity after the change, including tombstones. */
    row,
    conflicts: z.array(ContentConflict).optional(),
    resolvedConflictIds: z.array(z.string()).optional(),
  });

export const Change = z.discriminatedUnion("entity", [
  change("task", Task),
  change("note", Note),
  change("folder", Folder),
  change("project", Project),
  change("area", Area),
  change("reminder", Reminder),
]);
export type Change = z.infer<typeof Change>;

export const PushRequest = z.object({
  protocolVersion: z.number().int(),
  deviceId: z.string().min(1),
  mutations: z.array(MutationEnvelope).min(1).max(200),
});
export type PushRequest = z.infer<typeof PushRequest>;

export const PushRejection = z.object({
  mutationId: z.uuid(),
  code: z.enum(["validation", "not_found", "internal"]),
  message: z.string(),
});
export type PushRejection = z.infer<typeof PushRejection>;

export const PushConflict = z.object({
  mutationId: z.uuid(),
  entity: EntityKind,
  id: EntityId,
  /** The revision the server holds, which is newer than the mutation's `baseRevision`. */
  serverRevision: z.number().int(),
  /** The server stored the rejected content for recovery (notes only). */
  conflictId: z.string().optional(),
});
export type PushConflict = z.infer<typeof PushConflict>;

export const PushResponse = z.object({
  /** Applied, or previously applied (a retry). Includes mutations against tombstones, which are acknowledged but not applied. */
  acknowledged: z.array(z.uuid()),
  /** Acknowledged too (do not retry) but the write was refused because `baseRevision` was stale. */
  conflicts: z.array(PushConflict),
  /** Not acknowledged. The client retries only these. */
  rejected: z.array(PushRejection),
  cursor: z.number().int().nonnegative(),
});
export type PushResponse = z.infer<typeof PushResponse>;

export const PullRequest = z.object({
  protocolVersion: z.number().int(),
  deviceId: z.string().min(1),
  after: z.number().int().nonnegative(),
  limit: z.number().int().positive().max(1000).default(500),
});
export type PullRequest = z.infer<typeof PullRequest>;
export type PullRequestInput = z.input<typeof PullRequest>;

export const PullResponse = z.object({
  cursor: z.number().int().nonnegative(),
  changes: z.array(Change),
  hasMore: z.boolean(),
});
export type PullResponse = z.infer<typeof PullResponse>;
